import {
  PRESET_CARD_ACTIONS_GAP,
  PRESET_CARD_CLASS,
  PRESET_GRID_CLASS,
  PRESET_PREVIEW_FRAME_CLASS,
} from 'modules/manager/ui/components/preset-card-metrics';
import React from 'react';

/** Ширина кнопки карточки — снята в браузере: «Открыть» у админа, «Просмотр» у остальных. */
const OPEN_BUTTON_WIDTH = { admin: 91, viewer: 102 };

/** Строка подписи `body-2` / `body-3` в карточке — 15px; плашка чуть ниже, чтобы две строки не слились в одну. */
const TEXT_LINE_HEIGHT = 15;
const TEXT_PLATE_HEIGHT = 11;

interface PresetCardSkeletonProps {
  /** Как у `MediaManagerPresetCard`: админ видит «Открыть», «Редактировать» и «Удалить». */
  isAdmin?: boolean;
  /** Как у `MediaManagerPresetCard`: иконка «Дублировать в свой турнир». */
  canDuplicate?: boolean;
  /**
   * Число иконок действий, если оно не выводится из `isAdmin`/`canDuplicate` — у шаблона организации
   * (`MediaManagerTemplateListElement`) набор свой: «Переместить», «Скачать», «Редактировать», «Удалить».
   */
  iconsCount?: number;
}

/**
 * Карточка пресета на время загрузки: те же рамка, квадрат превью, две строки
 * подписи и ряд «кнопка + иконки» с тем же набором иконок, что будет у настоящей
 * карточки. Поэтому высота карточки-скелетона равна высоте настоящей.
 */
export const PresetCardSkeleton: React.FC<PresetCardSkeletonProps> = ({
  isAdmin = false,
  canDuplicate = false,
  iconsCount: iconsCountOverride,
}) => {
  // «Скачать файл» есть всегда; «Дублировать» — по праву; «Редактировать» и «Удалить» — у админа.
  const iconsCount = iconsCountOverride ?? 1 + (canDuplicate ? 1 : 0) + (isAdmin ? 2 : 0);

  return (
    <div className={PRESET_CARD_CLASS} data-testid="preset-card-skeleton">
      <div className={PRESET_PREVIEW_FRAME_CLASS}>
        <div className="skeleton-block absolute inset-0" />
      </div>

      <div className="flex min-w-0 flex-col">
        <TextPlate width="70%" />
        <TextPlate width="40%" />
      </div>

      <div className="flex items-center justify-between">
        <div className="skeleton-block h-9" style={{ width: isAdmin ? OPEN_BUTTON_WIDTH.admin : OPEN_BUTTON_WIDTH.viewer }} />
        <div className="flex items-center" style={{ gap: PRESET_CARD_ACTIONS_GAP }}>
          {Array.from({ length: iconsCount }, (_, index) => (
            <div key={index} className="skeleton-block size-5" data-testid="preset-card-skeleton-icon" />
          ))}
        </div>
      </div>
    </div>
  );
};

const TextPlate: React.FC<{ width: string }> = ({ width }) => (
  <div className="flex items-center" style={{ height: TEXT_LINE_HEIGHT }}>
    <div className="skeleton-block" style={{ width, height: TEXT_PLATE_HEIGHT }} />
  </div>
);

interface PresetGridSkeletonProps extends PresetCardSkeletonProps {
  /** Сколько карточек нарисовать: 8 — два ряда при самой широкой сетке. */
  count?: number;
}

/** Сетка библиотеки пресетов на время загрузки — те же классы колонок, что у карточек. */
export const PresetGridSkeleton: React.FC<PresetGridSkeletonProps> = ({ count = 8, ...cardProps }) => (
  <div className={PRESET_GRID_CLASS} data-testid="preset-grid-skeleton" aria-hidden>
    {Array.from({ length: count }, (_, index) => (
      <PresetCardSkeleton key={index} {...cardProps} />
    ))}
  </div>
);

export default PresetGridSkeleton;
